import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import ChassisSection from 'components/common/recommendation-tiles/chassis-section';
import TonnageSection from 'components/common/recommendation-tiles/tonnage-section';
import RecommendationsStrip from 'components/common/recommendations-strip';
import {
  filterSystemTypesByFuel,
  pruneTypeSelectionsToFuels,
  toggleEnumFilterValue
} from 'components/equipment-page/ducted-facet-merge';
import { ExistingSystemType } from 'data/types';
import {
  restoreSystemCompatibleResults,
  setSystemRecommendationSelections,
  setSystemTonnageRecord,
  setWantCompositionPicks,
  SystemCompatibleResultsEntry
} from 'features/structure/structure-slice';
import {
  CABINET_CHASSIS_SIZE_FILTER_FIELD,
  DuctedSystemTypeSelection,
  setDuctedCombinationFilters,
  setDuctedFuelSelections,
  setDuctedSystemTypeSelections
} from 'features/ui';
import useDuctedFilterContextReset from 'hooks/use-ducted-filter-context-reset';
import useRecommendationSelectionsSave from 'hooks/use-recommendation-selections-save';
import { compositionWantsPackagedUnit } from 'utils/composition-picks';
import runCompatibilityCheck from 'utils/equipment-compatibility-check';
import {
  furnaceFuelToDucted,
  sameDuctedFuelSelections
} from 'utils/furnace-fuel';
import {
  annotateKeptSystemTypeLabel,
  DuctedFuelToken
} from 'utils/kept-system-type-annotation';
import { isDuctedOnlySystemPreference } from 'utils/project';
import { dwellingForSubtypeCode } from 'utils/recommendation-tiles-logic';
import { buildSystemCountsByCode } from 'utils/recommendation-system-counts';
import { RecommendationsStripVisibleSection } from 'utils/recommendations-strip-visible-section-types';
import { wantIdsAreEqual } from 'utils/equipment-preferences-complete';
import {
  buildComponentsKey,
  RecommendationSelections,
  resolveEffectiveRecommendedCodes,
  SavedRecommendationSelections
} from 'utils/recommendations-strip-logic';
import { CompositionPicks, FurnaceFuel } from 'utils/system-composition-types';
import {
  calcTonnageFromBtu,
  deriveEquipmentTonnage,
  isUnsureTonnage,
  parseSavedTonnage,
  resolveEffectiveTonnage,
  TonnageRecord
} from 'utils/tonnage-logic';
import {
  buildSizeRecordSelectionsPayload,
  withMirroredSizeRecords
} from 'utils/size-record-persist';
import { resolveSystemTonnageSeed } from 'utils/system-tonnage-seed';
import { selectDuctedPairLoads } from 'utils/ducted-pair-context';

type EquipmentStepRecommendationsProps = {
  system: ExistingSystemType;
  visibleSection?: RecommendationsStripVisibleSection;
  onRecommendationsChange?: (
    systemUniqueId: string,
    result: {
      matched: boolean;
      recommendedCodes: string[];
      wantOnlyCodes?: string[];
    }
  ) => void;
};

// The LES-3 checkbox is gone from this wrapper (AHA-6681 sub-build 3): every
// check is recommended-bounded, so the strip's checkbox contract collapses
// to a constant-true flag with an inert toggle. The CCB modal keeps the
// checkbox untouched.
const noopToggleUseRecommendations = () => {};

// Per-system host for the shared RecommendationsStrip on the Equipment
// Details step (AHA-6681). One instance per system so each strip gets
// its own hook state (the step maps over systems). Mirrors the CCB modal's
// reporting contract (reference-stable codes) and adds the LES-2 persistence
// wiring the modal doesn't have. Also renders the LES-4 compatibility-results
// panel below the strip. Since sub-build 3 the wants selection is THE
// gateway: the compatibility check always runs against the recommended
// codes, so the persisted summary is always the recommended-set facet — the
// LES-3 "Only show recommended system types" checkbox is removed here (it
// survives in the CCB modal browse context).
export default function EquipmentStepRecommendations({
  system,
  visibleSection = 'both',
  onRecommendationsChange
}: EquipmentStepRecommendationsProps) {
  const dispatch = useAppDispatch();
  // Reference-stable copy of the strip's reported codes (the CCB's
  // contentChanged guard): the strip reports on every recommendations
  // response, and a fresh-but-equal array must not churn renders or re-arm
  // the refetch debounce. Null = the strip hasn't reported yet — the live
  // refetch effect stays disarmed so a premature no-codes check can never
  // race the strip's first report and clobber restored results.
  const [recommendedCodes, setRecommendedCodes] = useState<string[] | null>(
    null
  );
  // Starts null and is only set from the strip's onSelectionsChange, so a
  // saved-selections restore never echo-saves (LES-2 contract).
  const [selections, setSelections] = useState<RecommendationSelections | null>(
    null
  );
  // Latest compatibility check for this system (AHA-6681 LES-4): live from
  // the debounced refetch below or the Next-click fetch, or restored from
  // the persisted JSON.
  const compatibleResults = useAppSelector(
    (state) => state.structure.compatibleResultsBySystem[system.uniqueId]
  );
  // The full-record mirror entry — the tonnage record's home (AHA-6681
  // tonnage §E). A ref twin lets the strip-emit handler below merge the
  // CURRENT record without joining callback deps.
  const selectionsEntry = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem[system.uniqueId]
  );
  const selectionsEntryRef = useRef(selectionsEntry);
  selectionsEntryRef.current = selectionsEntry;
  const tonnageRecord = selectionsEntry?.tonnage ?? null;
  const coolingBtu = useAppSelector(
    (state) => selectDuctedPairLoads(state, system.uniqueId).cooling
  );
  // Reactive (not the store-reading helper): the sizing-path block must
  // follow a live preference change.
  const isDuctedOnly = useAppSelector((state) =>
    isDuctedOnlySystemPreference(
      state.project.projectSettings.find(
        (ps) => ps.code === 'system_preference'
      )?.value
    )
  );
  // Dwelling mode for the strip's Blower-offer filter (Geoff ruling,
  // AHA-6681 dwelling filter): resolve the structure subtype's CODE by id
  // across the loaded structure types (code-keyed, never id-keyed — the
  // resolveDwellingSuggestion precedent). A null subtype or not-yet-loaded
  // structureTypes resolves to 'standard', matching Geoff's
  // unknown-subtype rule.
  const structureSubtypeId = useAppSelector(
    (state) => state.structure.structureSubtypeId
  );
  const structureTypes = useAppSelector((state) => state.data.structureTypes);
  const dwelling = useMemo(() => {
    const subtypeCode =
      structureSubtypeId != null
        ? structureTypes
            .flatMap((structureType) => structureType.subtypes)
            .find((subtype) => subtype.id === structureSubtypeId)?.code ?? null
        : null;
    return dwellingForSubtypeCode(subtypeCode);
  }, [structureSubtypeId, structureTypes]);

  // CHASSIS row (AHA-6796) plus the shared fuel filter state: chassis
  // still has its own row here; fuel is asked once in the want picker and
  // written into ui.ductedFilters.fuelSelections so Choose Systems and
  // the recommended-list / compatible-count narrowing stay in lockstep.
  // The context-reset hook still lands the equipment seeds while the user
  // is on this page (propane / chassis) so a first CS visit cannot clobber
  // an EE-made choice.
  const ductedFilters = useAppSelector((state) => state.ui.ductedFilters);
  const { boundUnionCodes, seedChassisSizes } = useDuctedFilterContextReset();
  const wantsPackagedUnit = useAppSelector((state) =>
    compositionWantsPackagedUnit(
      state.structure.wantCompositionPicksBySystem[system.uniqueId]
    )
  );
  const chassisSelected = useMemo(() => {
    const found = ductedFilters.filters.find(
      (filter) =>
        filter.field === CABINET_CHASSIS_SIZE_FILTER_FIELD &&
        filter.kind === 'enum'
    );
    return found && found.kind === 'enum' ? found.values : [];
  }, [ductedFilters.filters]);

  const handleWantFuelChange = useCallback(
    (fuel: FurnaceFuel | null) => {
      const next: DuctedFuelToken[] =
        fuel === null ? [] : [furnaceFuelToDucted(fuel)];
      if (sameDuctedFuelSelections(next, ductedFilters.fuelSelections)) {
        return;
      }
      dispatch(setDuctedFuelSelections(next));
      const pruned: DuctedSystemTypeSelection[] = pruneTypeSelectionsToFuels(
        ductedFilters.systemTypeSelections,
        next
      );
      if (pruned !== ductedFilters.systemTypeSelections) {
        dispatch(setDuctedSystemTypeSelections(pruned));
      }
    },
    [dispatch, ductedFilters.fuelSelections, ductedFilters.systemTypeSelections]
  );

  // Same enum upsert as the CS faceted control (shared helper): this IS
  // the cabinetChassisSize combination filter, surfaced.
  const handleChassisToggle = useCallback(
    (size: string) => {
      dispatch(
        setDuctedCombinationFilters(
          toggleEnumFilterValue(
            ductedFilters.filters,
            CABINET_CHASSIS_SIZE_FILTER_FIELD,
            size
          )
        )
      );
    },
    [dispatch, ductedFilters.filters]
  );

  const keptComponentUniqueIds = useMemo(
    () => system.components.filter((c) => !c.replacing).map((c) => c.uniqueId),
    [system.components]
  );
  // Kept component classes feed the kept-column "(keeping)" row annotation.
  const keptComponentTypes = useMemo(
    () => system.components.filter((c) => !c.replacing).map((c) => c.type),
    [system.components]
  );
  // The nonce in the CCB's specsVersion slot is a save-COMPLETION version
  // (AHA-6681 QA F2), not a spec-edit key: keep/replace flips and component
  // add/removes ride the 500ms debounced saveBaseStructure autosave, so the
  // flip-triggered context GET is one-mutation-behind and reconciles against
  // the PRE-save derivation — the fingerprint-overwrite reseed would never
  // run in-session, and the stale selections would re-persist under the new
  // fingerprint on the next mount. The autosave watcher bumps the version
  // AFTER such a save lands, so the refired GET reads guaranteed-current DB
  // (no stale-DB race — unlike a spec-keyed refetch would have). Spec edits
  // still deliberately do NOT refetch the strip context on this step —
  // accepted limitation, flagged for LES-5 QA.
  const equipmentSaveVersion = useAppSelector(
    (state) => state.structure.equipmentSaveVersion
  );
  const componentsKey = useMemo(
    () => buildComponentsKey(system.components, equipmentSaveVersion),
    [system.components, equipmentSaveVersion]
  );

  // The tonnage-filter seed for the check params (AHA-6681 tonnage-filtered
  // panel counts), resolved from the same inputs the tonnage section
  // consumes. The debounce keys on the VALUE string, not the object: mirror
  // writes mint fresh-but-equal records and must not re-arm it (the
  // keptComponentIdsKey rationale).
  const tonnageSeed = useMemo(
    () =>
      resolveSystemTonnageSeed({
        system,
        record: tonnageRecord,
        coolingBtu
      }),
    [tonnageRecord, system, coolingBtu]
  );
  const tonnageSeedKey = tonnageSeed
    ? `${tonnageSeed.min}-${tonnageSeed.max}`
    : 'none';

  // Debounced live refetch of the compatible-systems check (AHA-6681):
  // re-runs while the user sits on the step, keyed on exactly what the
  // check consumes — reported codes, kept set, and componentsKey (a
  // component add/remove changes server-side state without necessarily
  // changing the other two). Spec edits deliberately do NOT key it (see the
  // componentsKey comment above). The thunk dedupes identical params, so
  // the Next-click ref-path fetch stays a harmless safety net; cleanup
  // clears only this timer and can never cancel that fetch.
  const keptComponentIdsKey = keptComponentUniqueIds.join(',');
  useEffect(() => {
    if (recommendedCodes === null) return undefined;
    const timer = setTimeout(() => {
      dispatch(
        runCompatibilityCheck({
          systemUniqueId: system.uniqueId,
          keptComponentUniqueIds,
          // Always the recommended set (AHA-6681 sub-build 3, wants
          // gateway): the persisted LES-4 summary is always the
          // recommended-set facet. Empty codes make the thunk record an
          // honest zero without a POST.
          systemTypeCodes: recommendedCodes,
          componentsKey,
          tonnageSeed
        })
      );
    }, 600);
    return () => clearTimeout(timer);
    // keptComponentUniqueIds is keyed by its serialization: the memo gets a
    // fresh identity on ANY system.components write (spec edits included),
    // and a fresh-but-equal array must not re-arm the debounce. Same for
    // tonnageSeed via tonnageSeedKey: a tile tap / dismissal changes the
    // key and re-arms; reference churn alone never does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    system.uniqueId,
    recommendedCodes,
    keptComponentIdsKey,
    componentsKey,
    tonnageSeedKey
  ]);

  const handleRecommendationsChange = (result: {
    matched: boolean;
    recommendedCodes: string[];
    wantOnlyCodes?: string[];
  }) => {
    // Want-only fallback (AHA-6681): only the strip's no-anchor report
    // carries wantOnlyCodes — the compatibility check then runs bounded to
    // the selected want types' own code union instead of skipping. An
    // anchored zero (matched with recommendedCodes: []) carries no
    // wantOnlyCodes and deliberately stays an honest zero.
    const effectiveCodes = resolveEffectiveRecommendedCodes(
      result.recommendedCodes,
      result.wantOnlyCodes
    );
    setRecommendedCodes((prev) => {
      const contentChanged =
        !prev ||
        prev.length !== effectiveCodes.length ||
        prev.some((code, index) => code !== effectiveCodes[index]);
      return contentChanged ? effectiveCodes : prev;
    });
    onRecommendationsChange?.(system.uniqueId, result);
  };

  // Restore hop (LES-4): the strip's context GET is the only reader of the
  // persisted JSON, so a persisted lastResults reaches Redux (and the
  // Choose Systems tab signal) through this callback. The reducer is
  // set-if-absent — a re-fired restore never clobbers a live fetch.
  const handleSavedSelectionsRestore = useCallback(
    (saved: SavedRecommendationSelections) => {
      // Keep the full-record mirror in sync with an applied restore (AHA-6681
      // batch 1, widened for tonnage) — the load-time hydration may predate
      // this save. Explicit picks so the server-managed lastResults never
      // enters the mirror.
      const parsedTonnage = parseSavedTonnage(saved.tonnage);
      dispatch(
        setSystemRecommendationSelections({
          systemUniqueId: system.uniqueId,
          selections: {
            wantIds: saved.wantIds ?? [],
            haveId: saved.haveId,
            refrigerant: saved.refrigerant,
            age: saved.age,
            useRecommendations: saved.useRecommendations,
            fingerprint: saved.fingerprint,
            virtualCodes: saved.virtualCodes,
            virtualFuel: saved.virtualFuel,
            ...(parsedTonnage ? { tonnage: parsedTonnage } : {})
          }
        })
      );
      if (!saved.lastResults) return;
      const { fetchedAt, alternatives, kept } = saved.lastResults;
      dispatch(
        restoreSystemCompatibleResults({
          systemUniqueId: system.uniqueId,
          // Restored entries carry no requestBodyHash on purpose — the first
          // live check after a restore always refetches once.
          results: {
            alternatives: { status: 'loaded', fetchedAt, ...alternatives },
            ...(kept ? { kept: { status: 'loaded', fetchedAt, ...kept } } : {})
          }
        })
      );
    },
    [dispatch, system.uniqueId]
  );

  // Persisted payload (LES-2): useRecommendations rides along as a constant
  // true — the stored rows are always recommended-bounded now, and the flag
  // keeps the persisted record honest for older readers. While `selections`
  // is null there is nothing to merge into — no save (a user gesture always
  // supplies one via the strip's emits).
  const savePayload = useMemo(
    () => (selections ? { ...selections, useRecommendations: true } : null),
    [selections]
  );
  useRecommendationSelectionsSave(system.uniqueId, savePayload);

  // Every strip selection emit also updates the full-record mirror (AHA-6681
  // batch 1, widened for tonnage) so the Existing Equipment page's Next gate
  // and the tonnage surfaces react live. useRecommendations rides along as
  // the same constant true the persisted payload carries. The strip knows
  // nothing about tonnage, so the mirror's current record merges into every
  // emit — the PATCH replaces the whole record and the reducer replaces the
  // whole mirror entry, so an unmerged emit would wipe it. This is also what
  // makes manual/dialog picks survive the fingerprint-overwrite emit (§B
  // row 9, approved deviation): the overwrite re-derives wants/have but the
  // tonnage record rides through untouched. Derived (equipment/calc) sizes
  // never became a record in the first place, so they re-derive normally.
  const handleSelectionsChange = useCallback(
    (next: RecommendationSelections) => {
      const merged = withMirroredSizeRecords(next, selectionsEntryRef.current);
      setSelections(merged);
      dispatch(
        setSystemRecommendationSelections({
          systemUniqueId: system.uniqueId,
          selections: { ...merged, useRecommendations: true }
        })
      );
    },
    [dispatch, system.uniqueId]
  );

  const handleCompositionPicksChange = useCallback(
    (picks: CompositionPicks, wantIds: number[]) => {
      dispatch(
        setWantCompositionPicks({
          systemUniqueId: system.uniqueId,
          picks
        })
      );
      if (wantIds.length === 0) return;
      const { current } = selectionsEntryRef;
      const currentWantIds = current?.wantIds ?? [];
      if (wantIdsAreEqual(currentWantIds, wantIds)) return;
      dispatch(
        setSystemRecommendationSelections({
          systemUniqueId: system.uniqueId,
          selections: {
            ...(current ?? { wantIds: [] }),
            wantIds
          }
        })
      );
    },
    [dispatch, system.uniqueId]
  );

  // Tonnage derivations (AHA-6681 tonnage §B): equipment from the system's
  // live components, calc from the L&S block-load cooling BTU, resolved by
  // resolveEffectiveTonnage (explicit pick, else the calc, else equipment).
  const equipmentTonnage = useMemo(
    () => deriveEquipmentTonnage(system.components),
    [system.components]
  );
  const calcTonnage = useMemo(
    () => calcTonnageFromBtu(coolingBtu),
    [coolingBtu]
  );
  const effectiveTonnage = useMemo(
    () =>
      resolveEffectiveTonnage({
        record: tonnageRecord,
        equipment: equipmentTonnage,
        calc: calcTonnage
      }),
    [tonnageRecord, equipmentTonnage, calcTonnage]
  );

  const systemCountsByCode = useMemo(
    () =>
      isUnsureTonnage(tonnageRecord)
        ? null
        : buildSystemCountsByCode(compatibleResults),
    [tonnageRecord, compatibleResults]
  );

  // A tonnage gesture (tile tap / sizing-path pick) updates the mirror and
  // rides the same debounced selections PATCH as every strip change. When
  // the strip hasn't emitted yet this session, the payload rebuilds from the
  // mirror entry so the save never drops previously-persisted fields.
  const handleTonnageRecordChange = useCallback(
    (tonnage: TonnageRecord) => {
      dispatch(
        setSystemTonnageRecord({ systemUniqueId: system.uniqueId, tonnage })
      );
      setSelections((prev) =>
        prev
          ? { ...prev, tonnage }
          : buildSizeRecordSelectionsPayload(selectionsEntryRef.current, {
              tonnage
            })
      );
    },
    [dispatch, system.uniqueId]
  );

  // Dual kept/alternatives summary (AHA-6681): the panel is always two
  // side-by-side columns — kept (left) | alternatives (right) — in every
  // state; only a narrow-container CSS breakpoint may stack them. Each
  // column renders its OWN loading/loaded/empty/error state from its slice
  // entry, so one side never pops in silently. Rows from the last COMPLETED
  // fetches stay visible under a refresh's loading line; no record at all
  // (never checked, nothing restored) renders nothing.
  const keptEntry = compatibleResults?.kept;
  const altEntry = compatibleResults?.alternatives;
  const nothingKept = keptComponentUniqueIds.length === 0;

  // Render-time bound for the stale window between a recommendations change
  // and the refetch landing: stored rows are already bounded once that fetch
  // completes, but a codes change must narrow the panel instantly.
  // Zero-count rows within the recommended set stay visible (requirement C).
  // The Fuel selection then narrows STRICTLY (AHA-6796 — the CS display
  // rule, filterSystemTypesByFuel): the counts must never show a
  // wrong-fuel type while a fuel is picked.
  const visibleSystemTypesOf = (
    entry: SystemCompatibleResultsEntry | undefined
  ) =>
    filterSystemTypesByFuel(
      recommendedCodes !== null
        ? (entry?.systemTypes ?? []).filter((systemType) =>
            recommendedCodes.includes(systemType.code)
          )
        : entry?.systemTypes ?? [],
      ductedFilters.fuelSelections
    );

  // While a fuel selection is active the server totalCount includes
  // wrong-fuel rows, so each column's total renders as the sum of its
  // VISIBLE facet counts instead (the CS recommendedAllTypesCount
  // convention). Display-only — fuel never rides the check POST (it would
  // narrow the persisted summary → the CS bound/resultsKey → re-fire the
  // context reset over the very state that drove it).
  const fuelNarrowedCounts = ductedFilters.fuelSelections.length > 0;

  // Kept-column rows render each label segment whose class the user is
  // keeping as "Customer's <segment>" with a subtle style (AHA-6681 display
  // titles — formerly an italic "(keeping)" suffix); a null annotation (no
  // kept-class match) keeps the plain single-text-node row. The
  // alternatives column never annotates.
  const renderGroupRows = (
    entry: SystemCompatibleResultsEntry,
    resultSet: 'kept' | 'alternatives'
  ) => {
    const visibleSystemTypes = visibleSystemTypesOf(entry);
    return (
      <div className='mb-1'>
        {visibleSystemTypes.map((systemType) => {
          const annotatedSegments =
            resultSet === 'kept'
              ? annotateKeptSystemTypeLabel(
                  systemType.label,
                  systemType.code,
                  keptComponentTypes
                )
              : null;
          return (
            <div key={systemType.code} className='text-xs text-gray-700'>
              {annotatedSegments
                ? annotatedSegments.map((segment, index) => (
                    <span key={segment.text}>
                      {index > 0 && ' + '}
                      {segment.keeping ? (
                        <span className='italic text-gray-500'>
                          Customer&apos;s {segment.text}
                        </span>
                      ) : (
                        segment.text
                      )}
                    </span>
                  ))
                : systemType.label}{' '}
              — {systemType.count}
            </div>
          );
        })}
        <div className='mt-1 text-xs font-semibold text-gray-700'>
          Total:{' '}
          {fuelNarrowedCounts
            ? visibleSystemTypes.reduce(
                (sum, systemType) => sum + systemType.count,
                0
              )
            : entry.totalCount}
        </div>
      </div>
    );
  };

  // One column's body, per the per-column state matrix. The nothing-kept
  // check reads the LIVE prop (not the slice) and wins over any stale kept
  // entry. A kept set with NO entry yet (the debounce window before the
  // refetch's begin, or a restored alternatives-only record after the kept
  // set changed) honestly reads as pending: the refetch that mints the
  // entry is imminent once the strip has reported.
  const renderColumnBody = (
    resultSet: 'kept' | 'alternatives',
    entry: SystemCompatibleResultsEntry | undefined
  ) => {
    if (resultSet === 'kept' && nothingKept) {
      return (
        <div className='text-xs text-gray-500'>
          No equipment marked as keeping
        </div>
      );
    }
    return (
      <>
        {entry &&
          entry.fetchedAt !== null &&
          (entry.totalCount > 0 ? (
            renderGroupRows(entry, resultSet)
          ) : (
            <div className='text-xs text-gray-500'>
              {resultSet === 'kept' ? '0 compatible pairings' : '0 systems'}
            </div>
          ))}
        {(!entry || entry.status === 'loading') && (
          <div className='text-xs text-gray-500'>Checking…</div>
        )}
        {entry?.status === 'error' && (
          <div className='text-xs text-gray-400'>
            Couldn&apos;t check compatible systems
          </div>
        )}
      </>
    );
  };

  // Shared continue-hint, kept as an ADDITION under the per-column empty
  // messages: only when both columns are honestly zero — alternatives
  // loaded at zero AND (nothing kept OR kept loaded at zero). A loading or
  // errored entry suppresses it.
  const columnIsZero = (entry: SystemCompatibleResultsEntry | undefined) =>
    !!entry && entry.status === 'loaded' && entry.totalCount === 0;
  const showContinueHint =
    columnIsZero(altEntry) && (nothingKept || columnIsZero(keptEntry));

  const sizeSlot = (
    <>
      <TonnageSection
        effective={effectiveTonnage}
        equipment={equipmentTonnage}
        calc={calcTonnage}
        record={tonnageRecord}
        onRecordChange={handleTonnageRecordChange}
        showSizingPath={isDuctedOnly}
      />
      {boundUnionCodes.length > 0 && !wantsPackagedUnit && (
        <ChassisSection
          selected={chassisSelected}
          source={ductedFilters.chassisFilterSource}
          seedSizes={seedChassisSizes}
          onToggle={handleChassisToggle}
        />
      )}
    </>
  );

  return (
    <>
      <RecommendationsStrip
        systemUniqueId={system.uniqueId}
        componentsKey={componentsKey}
        useRecommendations
        onToggleUseRecommendations={noopToggleUseRecommendations}
        showUseRecommendationsCheckbox={false}
        onRecommendationsChange={handleRecommendationsChange}
        onSelectionsChange={handleSelectionsChange}
        onSavedSelectionsRestore={handleSavedSelectionsRestore}
        // AHA-6681 tile panel: have component tiles + readout, want
        // system-type tiles, flat deduped recommendations. The CCB modal
        // keeps the default dropdown contract.
        variant='tiles'
        visibleSection={visibleSection}
        dwelling={dwelling}
        // Display-only fuel narrowing of the recommendations list
        // (AHA-6796): the same shared selection the want picker writes
        // and the Choose Systems chip row edits; never a fetch dep.
        wantFuelNarrow={ductedFilters.fuelSelections}
        onWantFuelChange={handleWantFuelChange}
        onCompositionPicksChange={handleCompositionPicksChange}
        systemCountsByCode={systemCountsByCode}
        tonnageSlot={sizeSlot}
      />
      {visibleSection !== 'have' && compatibleResults && (
        <div className='mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3'>
          <div className='mb-1 text-sm font-semibold text-gray-700'>
            Compatible systems
          </div>
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
            <div data-testid='compatible-kept-column'>
              <div className='mb-1 text-xs font-semibold text-gray-600'>
                Pairs with what you&apos;re keeping
              </div>
              {renderColumnBody('kept', keptEntry)}
            </div>
            <div data-testid='compatible-alternatives-column'>
              <div className='mb-1 text-xs font-semibold text-gray-600'>
                Full system alternatives
              </div>
              {renderColumnBody('alternatives', altEntry)}
            </div>
          </div>
          {showContinueHint && (
            <div className='mt-2 text-xs text-gray-500'>
              No compatible systems found — you can still continue
            </div>
          )}
        </div>
      )}
    </>
  );
}
